import { Duration } from "luxon";
import {
  DoorGroupDto,
  EquipmentTypeDto,
  GetVendorDto,
  VendorListDto,
} from "mock-api/src/shared-v2-types";
import { ChangesHistory } from "src/app/core/history.model";
import { SiteReference } from "src/app/core/sites";
import { TimeOfDay } from "src/app/core/time-of-day.model";
import { Weekdays } from "src/app/core/weekdays.model";
import { getApiDetailsDecorator, parseDuration } from "src/utils";
import {
  BaseVendor,
  getVendorRouteUrl,
  VendorReference,
} from "./base-vendor.model";

const api = getApiDetailsDecorator<VendorListDto>();

export class Vendor extends BaseVendor implements VendorReference {
  private constructor(args: ClassProperties<Vendor>) {
    super(args);

    this.id = args.id;
    this.name = args.name;
    this.number = args.number;
    this.reservationCount = args.reservationCount;
    this.equipmentGroup = args.equipmentGroup;
    this.doorGroupOverride = args.doorGroupOverride;
    this.site = args.site;

    this.displayName = getVendorDisplayName(this.name, this.number);
  }

  @api() public readonly id: VendorReference["id"];
  @api() public readonly name: string;
  @api({ key: "vendorNumber" }) public readonly number: string;
  @api() public readonly reservationCount: number;
  @api({ key: "equipmentType" })
  public readonly equipmentGroup: EquipmentTypeDto | null;
  @api({ key: "doorGroup" })
  public readonly doorGroupOverride: DoorGroupDto | null;

  public readonly site: SiteReference;
  public readonly displayName: string;

  public static deserialize(data: VendorListDto, site: SiteReference): Vendor {
    return new Vendor({
      ...BaseVendor.deserializeBase(data),
      doorGroupOverride: data.doorGroup,
      endTime: data.endTime ? TimeOfDay.deserialize(data.endTime) : null,
      equipmentGroup: data.equipmentType,
      id: data.id,
      name: data.name,
      number: data.vendorNumber,
      maxAppointmentDoorCount: data.doorCount,
      reservationCount: data.reservationCount,
      startTime: data.startTime ? TimeOfDay.deserialize(data.startTime) : null,
      weekdays: Weekdays.deserialize(data.daysOfWeek),
      displayName: getVendorDisplayName(data.name, data.vendorNumber),
      site,
    });
  }

  public static deserializeList(
    value: VendorListDto[],
    site: SiteReference,
  ): readonly Vendor[] {
    return value.map((x) => Vendor.deserialize(x, site));
  }

  public getRouteUrl(...childPaths: string[]): string {
    return getVendorRouteUrl(this, ...childPaths);
  }
}

const apiDetails = getApiDetailsDecorator<GetVendorDto>();
export class VendorDetails extends BaseVendor {
  private constructor(
    args: Omit<ClassProperties<VendorDetails>, "displayName">,
  ) {
    super(args);

    this.active = args.active;
    this.customUnloadDurationPerUnit = args.customUnloadDurationPerUnit;
    this.doorGroupOverride = args.doorGroupOverride;
    this.equipmentGroup = args.equipmentGroup;
    this.history = args.history;
    this.id = args.id;
    this.maximumCustomUnloadDurationPerUnit =
      args.maximumCustomUnloadDurationPerUnit;
    this.name = args.name;
    this.number = args.number;
    this.ownerCode = args.ownerCode;

    this.displayName = getVendorDisplayName(this.name, this.number);
  }
  @apiDetails() public readonly active: boolean;
  @apiDetails({ key: "customMinutesPerUnit" })
  public readonly customUnloadDurationPerUnit: Duration | null;
  @apiDetails({ key: "doorGroup" })
  public readonly doorGroupOverride: DoorGroupDto | null;
  @apiDetails({ key: "equipmentType" })
  public readonly equipmentGroup: EquipmentTypeDto | null;
  @apiDetails() public readonly history: readonly ChangesHistory[];
  @apiDetails() public readonly id: VendorReference["id"];
  @apiDetails({ key: "maxCalcCustomMinutesPerUnit" })
  public readonly maximumCustomUnloadDurationPerUnit: Duration | null;
  @apiDetails() public readonly name: string;
  @apiDetails({ key: "vendorNumber" }) public readonly number: string;
  @apiDetails() public readonly ownerCode: string;

  public readonly displayName: string;

  public static deserialize(data: GetVendorDto): VendorDetails {
    return new VendorDetails({
      ...BaseVendor.deserializeBase(data),
      id: data.id,
      name: data.name,
      number: data.vendorNumber,
      equipmentGroup: data.equipmentType,
      doorGroupOverride: data.doorGroup,
      active: data.active,
      ownerCode: data.ownerCode,
      customUnloadDurationPerUnit: parseDuration(
        data.customMinutesPerUnit,
        "minutes",
      ),
      maximumCustomUnloadDurationPerUnit: parseDuration(
        data.maxCalcCustomMinutesPerUnit,
        "minutes",
      ),
      history: data.history
        ? ChangesHistory.deserializeList<HistoryFieldName>(data.history, {
            fieldDefinitions: { maxLoadCount: { label: "Maximum Load Count" } },
          })
        : [],
    });
  }
}

type HistoryFieldName = StringKeys<GetVendorDto>;

type CommonApiVendorFields = GetVendorDto & VendorListDto;

export function deserializePurchaseOrderVendor(
  vendor: Pick<
    CommonApiVendorFields,
    | "allowSameDayAppointment"
    | "appointmentNotificationEmails"
    | "customMinutesPerUnit"
    | "id"
    | "maxCalcMinutesPerUnit"
    | "maxLoadCount"
    | "minutesPerUnit"
    | "name"
    | "offerUnreservedSlots"
    | "vendorNumber"
  >,
  { site }: { readonly site: SiteReference },
): PurchaseOrderVendor {
  const id = vendor.id;
  return {
    ...deserializeVendorDisplayName(vendor),
    areUnreservedSlotsOffered: vendor.offerUnreservedSlots,
    contactEmails: vendor.appointmentNotificationEmails || null,
    customUnloadDurationPerUnit: parseDuration(
      vendor.customMinutesPerUnit,
      "minutes",
    ),
    getRouteUrl: (...childPaths) =>
      getVendorRouteUrl({ id, site }, ...childPaths),
    id,
    isSameDayAppointmentAllowed: vendor.allowSameDayAppointment,
    maximumLoadCount: vendor.maxLoadCount ?? null,
    maximumUnloadDurationPerUnit: parseDuration(
      vendor.maxCalcMinutesPerUnit,
      "minutes",
    ),
    name: vendor.name,
    number: vendor.vendorNumber,
    unloadDurationPerUnit: parseDuration(vendor.minutesPerUnit, "minutes"),
  };
}

type CommonVendorProperties = Vendor & VendorDetails;

export interface PurchaseOrderVendor extends Pick<
  CommonVendorProperties,
  | "areUnreservedSlotsOffered"
  | "contactEmails"
  | "customUnloadDurationPerUnit"
  | "displayName"
  | "id"
  | "isSameDayAppointmentAllowed"
  | "maximumLoadCount"
  | "maximumUnloadDurationPerUnit"
  | "name"
  | "number"
  | "unloadDurationPerUnit"
> {
  getRouteUrl(...childPaths: string[]): string;
}

export function deserializeVendorDisplayName(
  vendor: Pick<VendorListDto, "name" | "vendorNumber">,
): Pick<Vendor, "displayName"> {
  return {
    displayName: getVendorDisplayName(vendor.name, vendor.vendorNumber),
  };
}

export function getVendorDisplayName(name: string, number: string): string {
  return `${name} (${number})`;
}
